import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "../../components/Icon";
import { PuzzlePlayer } from "../../components/puzzles/PuzzlePlayer";
import { ReflectionPlayer } from "../../components/puzzles/ReflectionPlayer";
import {
  Button,
  Card,
  EmptyState,
  Pill,
  SectionHeading,
} from "../../components/ui";
import { usePortal } from "../../state/PortalContext";
import type { Attempt } from "../../types/domain";

const stages = [
  { label: "Story", icon: "ScrollText" },
  { label: "Goal", icon: "Compass" },
  { label: "Practice", icon: "BookOpen" },
  { label: "Puzzle", icon: "Gamepad2" },
  { label: "Reflect", icon: "BrainCircuit" },
  { label: "Reward", icon: "Trophy" },
];

export function QuestPage() {
  const { questId } = useParams();
  const { curriculum, startAttempt, submitAttempt, progress } = usePortal();
  const quest = curriculum.quests.find((candidate) => candidate.id === questId);
  const region = curriculum.regions.find(
    (candidate) => candidate.id === quest?.regionId,
  );
  const [stage, setStage] = useState(0);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [instance, setInstance] = useState<unknown>(null);
  const [submission, setSubmission] = useState<Record<string, unknown> | null>(
    null,
  );
  const [result, setResult] = useState<{
    success: boolean;
    feedback: string;
    score: number;
    attempt: Attempt;
  } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!quest || !region) {
    return (
      <EmptyState
        icon="CircleHelp"
        title="Quest not found"
        detail="This route may have changed in a newer curriculum version."
        action={
          <Link to="/app/map">
            <Button>Return to map</Button>
          </Link>
        }
      />
    );
  }

  const beginPuzzle = async () => {
    setBusy(true);
    setError("");
    try {
      const bundle = await startAttempt(quest);
      setAttempt(bundle.attempt);
      setInstance(bundle.instance);
      setStage(3);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to begin.");
    } finally {
      setBusy(false);
    }
  };

  const completeQuest = async (reflection: string) => {
    if (!attempt || !submission) return;
    setBusy(true);
    setError("");
    try {
      const nextResult = await submitAttempt(
        attempt,
        instance,
        quest,
        submission,
        reflection,
      );
      setResult(nextResult);
      setStage(5);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to submit.");
    } finally {
      setBusy(false);
    }
  };

  const retry = () => {
    setAttempt(null);
    setInstance(null);
    setSubmission(null);
    setResult(null);
    setStage(2);
  };

  return (
    <div className="space-y-7">
      <div className="flex items-center gap-3">
        <Link
          to="/app/map"
          className="grid h-11 w-11 place-items-center rounded-xl border border-slate-800 bg-slate-900 text-slate-300 hover:border-slate-600 hover:text-white"
          aria-label="Return to world map"
        >
          <Icon name="ArrowLeft" size={19} />
        </Link>
        <div>
          <p className="text-xs font-bold" style={{ color: region.color }}>
            {region.name}
          </p>
          <p className="text-[10px] text-slate-500">
            Quest version {quest.version}
          </p>
        </div>
      </div>

      <SectionHeading
        eyebrow={quest.boss ? "Boss Battle" : "Interactive Quest"}
        title={quest.title}
        detail={quest.learningGoal}
        action={
          <div className="flex gap-2">
            <Pill tone="amber">{quest.xp} XP</Pill>
            <Pill tone="cyan">{quest.gradePoints} points</Pill>
          </div>
        }
      />

      <div className="scrollbar-thin overflow-x-auto">
        <ol className="flex min-w-[680px] gap-2" aria-label="Quest stages">
          {stages.map((item, index) => (
            <li key={item.label} className="flex-1">
              <button
                className={`flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl border px-3 text-xs font-bold transition ${
                  index === stage
                    ? "border-indigo-400/50 bg-indigo-500/15 text-white"
                    : index < stage
                      ? "border-emerald-400/20 bg-emerald-400/5 text-emerald-300"
                      : "border-slate-800 bg-slate-950/40 text-slate-600"
                }`}
                disabled={index > stage || index === 3 || index === 5}
                onClick={() => index < stage && setStage(index)}
              >
                <Icon
                  name={index < stage ? "Check" : item.icon}
                  size={16}
                />
                {item.label}
              </button>
            </li>
          ))}
        </ol>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-400/20 bg-rose-400/5 p-4 text-sm text-rose-200">
          {error}
        </div>
      )}

      {quest.templateId === "coming-soon" ? (
        <Card className="p-7">
          <EmptyState
            icon="WandSparkles"
            title="This expedition is coming soon"
            detail="The curriculum briefing is published so you can see where the journey leads, but its bespoke interactive puzzle has not been released yet."
            action={
              <Link to="/app/map">
                <Button variant="secondary">Explore another region</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <>
          {stage === 0 && (
            <StoryPanel
              eyebrow="Story hook"
              icon="ScrollText"
              title="A problem worth solving"
              body={quest.storyHook}
              button="Reveal the learning goal"
              onNext={() => setStage(1)}
            />
          )}
          {stage === 1 && (
            <StoryPanel
              eyebrow="Learning goal"
              icon="Compass"
              title="What you will learn"
              body={quest.learningGoal}
              button="Enter guided practice"
              onNext={() => setStage(2)}
            />
          )}
          {stage === 2 && (
            <StoryPanel
              eyebrow="Guided practice"
              icon="BookOpen"
              title="Prepare your strategy"
              body={quest.practice}
              button={busy ? "Preparing puzzle..." : "Start puzzle attempt"}
              onNext={() => void beginPuzzle()}
              disabled={busy}
              footer={
                <p className="text-xs text-slate-500">
                  Attempts used:{" "}
                  {
                    progress.attempts.filter(
                      (candidate) => candidate.questId === quest.id,
                    ).length
                  }
                  {quest.maxAttempts ? ` / ${quest.maxAttempts}` : " / unlimited"}
                </p>
              }
            />
          )}
          {stage === 3 && instance && (
            <PuzzlePlayer
              quest={quest}
              instance={instance}
              onLock={(nextSubmission) => {
                setSubmission(nextSubmission);
                setStage(4);
              }}
            />
          )}
          {stage === 4 && (
            <ReflectionPlayer
              definition={quest.reflectionConfig}
              fallbackPrompt={quest.reflectionPrompt}
              seed={attempt?.seed ?? 0}
              busy={busy}
              onBack={() => setStage(3)}
              onSubmit={(payload) => void completeQuest(payload)}
            />
          )}
          {stage === 5 && result && (
            <Card
              className={`celebration mx-auto max-w-4xl overflow-hidden border-2 p-8 text-center sm:p-12 ${
                result.success
                  ? "border-emerald-400/40"
                  : "border-amber-400/30"
              }`}
            >
              <div
                className={`mx-auto grid h-24 w-24 place-items-center rounded-[2rem] ${
                  result.success
                    ? "bg-emerald-400/15 text-emerald-300"
                    : "bg-amber-400/15 text-amber-300"
                }`}
              >
                <Icon
                  name={result.success ? "Trophy" : "Compass"}
                  size={45}
                />
              </div>
              <Pill
                tone={result.success ? "emerald" : "amber"}
                className="mt-6"
              >
                Score {result.score}
              </Pill>
              <h2 className="mt-4 font-display text-4xl font-bold text-white">
                {result.success ? "Quest mastered!" : "Useful attempt recorded"}
              </h2>
              <p className="mx-auto mt-4 max-w-2xl leading-7 text-slate-300">
                {result.feedback}
              </p>
              {result.success && (
                <div className="mx-auto mt-7 flex max-w-md justify-center gap-3">
                  <Pill tone="amber">+{result.attempt.awardedXp} XP</Pill>
                  <Pill tone="cyan">
                    +{result.attempt.awardedGradePoints} grade points
                  </Pill>
                </div>
              )}
              <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/app/map">
                  <Button variant={result.success ? "gold" : "secondary"}>
                    <Icon name="Map" size={18} />
                    Return to world map
                  </Button>
                </Link>
                <Button variant="secondary" onClick={retry}>
                  Try another attempt
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function StoryPanel({
  eyebrow,
  icon,
  title,
  body,
  button,
  onNext,
  disabled,
  footer,
}: {
  eyebrow: string;
  icon: string;
  title: string;
  body: string;
  button: string;
  onNext(): void;
  disabled?: boolean;
  footer?: React.ReactNode;
}) {
  return (
    <Card className="mx-auto max-w-4xl overflow-hidden">
      <div className="grid md:grid-cols-[.32fr_.68fr]">
        <div className="grid min-h-64 place-items-center bg-gradient-to-br from-indigo-500/25 to-violet-700/10 p-8">
          <div className="grid h-28 w-28 place-items-center rounded-[2rem] border border-indigo-300/20 bg-slate-950/50 text-indigo-200 shadow-glow">
            <Icon name={icon} size={49} />
          </div>
        </div>
        <div className="p-8 sm:p-10">
          <p className="font-retro text-[8px] uppercase tracking-[0.2em] text-cyan-300">
            {eyebrow}
          </p>
          <h2 className="mt-4 font-display text-3xl font-bold text-white">
            {title}
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-300">{body}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button onClick={onNext} disabled={disabled}>
              {button}
              <Icon name="ChevronRight" size={18} />
            </Button>
            {footer}
          </div>
        </div>
      </div>
    </Card>
  );
}
